'use client';

import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { formatCurrency, parseCurrency, sanitizeCurrencyInput } from './currency';
import type { CalculatorField, CalculatorFieldValues } from './types';

export const useCurrencyFields = (initialValues: CalculatorFieldValues) => {
  const [values, setValues] = useState<CalculatorFieldValues>(initialValues);

  const updateValue = (label: string, transform: (raw: string) => string) =>
    setValues(previous => ({ ...previous, [label]: transform(previous[label]) }));

  const handleChange = (label: string, value: string) => updateValue(label, () => sanitizeCurrencyInput(value));

  // Editing uses unformatted digits.
  const handleFocus = (label: string) => updateValue(label, raw => (raw === '' ? '' : String(parseCurrency(raw))));

  const handleBlur = (label: string) =>
    updateValue(label, raw => (raw === '' ? '' : formatCurrency(parseCurrency(raw))));

  const fieldProps = (field: CalculatorField, error?: string) => ({
    label: field.label,
    hint: field.hint,
    inputMode: 'decimal' as const,
    value: values[field.label],
    error,
    invalid: Boolean(error),
    onChange: (event: ChangeEvent<HTMLInputElement>) => handleChange(field.label, event.target.value),
    onFocus: () => handleFocus(field.label),
    onBlur: () => handleBlur(field.label),
  });

  return { values, reset: () => setValues(initialValues), fieldProps };
};

/** Lets a form component take the factory without restating `CalculatorInput`'s props. */
export type CurrencyFieldProps = ReturnType<typeof useCurrencyFields>['fieldProps'];
